import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { IAppointments, useGetAvailableTimesMutation } from '../../../../services/availableTimes.service';
import { RootState } from '../../../../store';
import {
  filterByDay,
  getAllAvailableDaysOfTheMonth,
  getMonth,
  getNextMonth,
  getShortedAvailableDays,
  getYear,
  groupScheduleAvailableTimes,
  GroupTimes
} from '../../../../lib/dayjs';

export const useAppointmentsAvailable = () => {
  const {
    calendar: { calendarsAvailables, doctorId },
    product: { hasTimeLimitSchedule }
  } = useSelector((state: RootState) => state);

  const [appointments, setAppointments] = useState<IAppointments>({ availableList: [], availableListAddress: [] });
  const [currentMonth, setCurrentMonth] = useState<string>(getMonth() || '');
  const [currentYear, setCurrentYear] = useState<string>(getYear() || '');
  const [getAvailableTimes, { data, isLoading }] = useGetAvailableTimesMutation();
  let retryNextMoth = true;

  useEffect(() => {
    if (calendarsAvailables.length === 0) return;
    getAvailableTimes({ calendarsAvailables, doctorId: doctorId ? doctorId : null, month: currentMonth, year: currentYear });
  }, [calendarsAvailables, currentMonth, currentYear, doctorId]);

  useEffect(() => {
    const nextMonth = getNextMonth();

    if (data !== undefined) {
      setAppointments({
        availableList: [...new Set([...appointments.availableList, ...data.availableList])].sort(),
        availableListAddress:
          appointments.availableListAddress.length === 0
            ? data.availableListAddress
            : appointments.availableListAddress.reduce((acc: { [key: string]: string[] }[], item) => {
                const calendarId = Object.keys(item)[0];
                const availableList = item[calendarId];
                const newAvailableList = data.availableListAddress.find(item => Object.keys(item)[0] === calendarId);
                if (newAvailableList) {
                  acc.push({ [calendarId]: [...new Set([...availableList, ...newAvailableList[calendarId]])].sort() });
                }
                return acc;
              }, [])
      });
      if (data.availableList.length < 2 && getMonth() === currentMonth && retryNextMoth === true) {
        retryNextMoth = false;
        setCurrentMonth(nextMonth);
      }
    }
  }, [data, currentMonth, currentYear]);

  return { appointments, setCurrentMonth, setCurrentYear, isLoading: isLoading, hasTimeLimitSchedule };
};

export const useShortedAvailableDays = (appointmentsAvailable: IAppointments) => {
  console.log('appointmentsAvailable', appointmentsAvailable);
  const [shortedAvailableDays, setShortedAvailableDays] = useState<string[]>([]);
  useEffect(() => {
    setShortedAvailableDays(getShortedAvailableDays(appointmentsAvailable));
  }, [appointmentsAvailable?.availableList]);

  return [shortedAvailableDays] as const;
};

export const useAllAvailableDaysOfTheMonth = (appointmentsAvailable: IAppointments) => {
  const [allAvailableDaysOfTheMonth, setAllAvailableDaysOfTheMonth] = useState<string[]>([]);
  useEffect(() => {
    setAllAvailableDaysOfTheMonth(getAllAvailableDaysOfTheMonth(appointmentsAvailable));
  }, [appointmentsAvailable?.availableList]);

  return [allAvailableDaysOfTheMonth] as const;
};

export const useScheduleAvailableTimeGroupedByPeriod = (appointments: IAppointments, day: string) => {
  const [scheduleAvailableTimeGroupedByPeriod, setScheduleAvailableTimeGroupedByPeriod] = useState<GroupTimes>({
    morning: { availableList: [], availableListAddress: [] },
    afternoon: { availableList: [], availableListAddress: [] },
    evening: { availableList: [], availableListAddress: [] }
  });

  useEffect(() => {
    setScheduleAvailableTimeGroupedByPeriod(groupScheduleAvailableTimes(filterByDay(day, appointments)));
  }, [appointments, day]);

  return [scheduleAvailableTimeGroupedByPeriod];
};
